import { t } from "i18next";
import {
  faCalendarAlt,
  faPaperPlane,
} from "@fortawesome/pro-regular-svg-icons";
import { faTimer } from "@fortawesome/pro-regular-svg-icons";
import {
  faCheckCircle,
  faTimesCircle,
  faLock,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { dateHelper } from "../../helpers";
import journeyFormHelper from "../../helpers/journeyFormHelper";
import { UserContextInterface } from "../../state/UserContext";
import { JourneyFormButtonArg } from "../../types/dashboard/JourneyFormDropDownItems";
import JourneyListingDto from "../../types/dtos/dashboards/manager/listings/journey/JourneyListingDto";
import listingHelper from "../../helpers/listingHelper";
import Tooltip from "../common/Tooltip";

interface JourneyFormItemProps {
  userContext: UserContextInterface;
  loggedInUserCurrentJourneyRef: string | null;
  journey: JourneyListingDto;
  isCompletedTab: boolean;
  onUpdateClick(journey: JourneyListingDto, arg?: JourneyFormButtonArg): void;
  onViewClick(journey: JourneyListingDto, arg?: JourneyFormButtonArg): void;
  onDualPrepItemClick(
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ): void;
}

function JourneyFormItem({
  userContext,
  loggedInUserCurrentJourneyRef,
  journey,
  isCompletedTab,
  onUpdateClick,
  onViewClick,
  onDualPrepItemClick,
}: JourneyFormItemProps) {
  const [hasButtonBeenClicked, setHasButtonBeenClicked] =
    useState<boolean>(false);
  const standardButtonText = isCompletedTab
    ? t("Common.View")
    : t("Common.Update");

  const isLockDualPrepMeetingForEmployeeEnabled =
    userContext.user.client.moduleConfigs.some(
      (c) =>
        c.key === "LockDualPrepMeetingForEmployee" &&
        c.value.toLowerCase() === "true"
    );

  const journeyNotStartedOrPartiallyCompleteStatus =
    journey.hasNotBeenStarted ||
    (journey.approvalStatusEnum !== null &&
      journey.approvalStatusEnum === "PARTIALLY-SAVED-JOURNEY");

  const hasPreventJourneySwitchConfigEnabled =
    userContext.user.client.moduleConfigs.findIndex(
      (cfg) =>
        cfg.key === "PreventManualJourneySwitching" &&
        cfg.value.trim().toLowerCase() === "true"
    ) >= 0;

  const dualPrepDropDownButtonItems =
    journeyFormHelper.getDualPrepDropdownButtonItems(
      journey,
      isCompletedTab,
      false,
      hasPreventJourneySwitchConfigEnabled
    );

  /** Handles the click event for non-dual prep journeys */
  const onStandardButtonClick = (e: React.MouseEvent) => {
    setHasButtonBeenClicked(true);
    if (isCompletedTab) {
      onViewClick(journey);
    } else {
      onUpdateClick(journey);
    }
  };

  const onDualPrepButtonClick = (
    e: React.MouseEvent,
    journey: JourneyListingDto,
    arg: JourneyFormButtonArg
  ) => {
    setHasButtonBeenClicked(true);
    onDualPrepItemClick(journey, arg);
  };

  // Upcoming rows are a forecast. Keep Update disabled so it cannot assign the
  // journey before the expected date (that path records a user-initiated JourneyLog).
  const isUpcoming = journey.overallStatus === "UPCOMING";

  // Disable switching to a not-started/partially-saved journey when PreventManualJourneySwitching
  // is enabled, unless it is the employee's currently assigned journey.
  const disableStandardButton =
    isUpcoming ||
    (journeyNotStartedOrPartiallyCompleteStatus &&
      hasPreventJourneySwitchConfigEnabled &&
      journey.journeyReference !== loggedInUserCurrentJourneyRef);

  const disablePrimaryDualPrepButton =
    dualPrepDropDownButtonItems && dualPrepDropDownButtonItems.length > 0
      ? dualPrepDropDownButtonItems[0].disabled
      : true;

  // Helper function to get dual prep status information
  const getDualPrepStatusInfo = () => {
    if (!journey.isDualPrepJourney) return null;

    // Need to check if the isEmpPlanningPartiallySaved, though the isDualPrepSubjectAnswerSet
    // will not be set at this point. Need to check there is only one AnswerSet and it is partially saved.
    const isEmpPlanningPartiallySaved =
      journey.answerSets.length === 1 &&
      journey.answerSets[0].statusEnum === "PARTIALLY-SAVED-JOURNEY";

    const doesEmpPlanningExist = journey.answerSets.some(
      (x) => x.isDualPrepSubjectAnswerSet
    );
    const employeeCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepSubjectAnswerSet && x.statusEnum === "DUAL-PREP-SUBMITTED"
    );
    const doesManPlanningExist = journey.answerSets.some(
      (x) => x.isDualPrepOtherAnswerSet
    );
    const managerCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepOtherAnswerSet && x.statusEnum === "DUAL-PREP-SUBMITTED"
    );
    const doesMeetingDocExist = journey.answerSets.some(
      (x) => x.isDualPrepMeetingDocAnswerSet
    );
    const meetingDoc = journey.answerSets.find(
      (x) => x.isDualPrepMeetingDocAnswerSet
    );
    const meetingDocCompleted = journey.answerSets.some(
      (x) =>
        x.isDualPrepMeetingDocAnswerSet && x.statusEnum === "FULLY-APPROVED"
    );

    // Get manager name (simplified for now - could be enhanced with actual manager data)
    const managerName = "Manager"; // This could be passed as prop or retrieved from context

    return {
      isEmpPlanningPartiallySaved,
      doesEmpPlanningExist,
      employeeCompleted,
      doesManPlanningExist,
      managerCompleted,
      doesMeetingDocExist,
      meetingDocCompleted,
      meetingDoc,
      managerName,
    };
  };

  const dualPrepInfo = getDualPrepStatusInfo();

  // Render dual prep content section
  const renderDualPrepContent = () => {
    if (!dualPrepInfo) return null;

    const {
      isEmpPlanningPartiallySaved,
      doesEmpPlanningExist,
      employeeCompleted,
      doesManPlanningExist,
      managerCompleted,
      doesMeetingDocExist,
      meetingDocCompleted,
      meetingDoc,
      managerName,
    } = dualPrepInfo;

    return (
      <div className="bg-white rounded-b-md px-4 py-3 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_1fr] gap-8 text-sm">
          {/* Dual Prep Journey indicator */}
          <div className="flex items-center">
            <span className="text-xs text-gray-600 bg-gray-100 rounded px-2 py-1">
              Dual Prep Journey
            </span>
          </div>
          {/* Your prep status */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={employeeCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                employeeCompleted,
                true,
                doesEmpPlanningExist || isEmpPlanningPartiallySaved
              )}`}
              size="lg"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">Your Prep</div>
              <div className="text-xs">
                <button
                  className={`${listingHelper.getDualPrepStatusTextColor(
                    employeeCompleted,
                    true,
                    doesEmpPlanningExist || isEmpPlanningPartiallySaved
                  )} hover:font-semibold underline font-medium transition-colors text-xs`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (employeeCompleted) {
                      onDualPrepButtonClick(e, journey, "VIEW-EMP-PLANNING");
                    } else {
                      onDualPrepButtonClick(
                        e,
                        journey,
                        doesEmpPlanningExist && !isEmpPlanningPartiallySaved
                          ? "COMPLETE-EMP-PLANNING"
                          : "START-EMP-PLANNING"
                      );
                    }
                  }}
                >
                  {employeeCompleted
                    ? "View"
                    : doesEmpPlanningExist || isEmpPlanningPartiallySaved
                    ? "Continue"
                    : "Start"}
                </button>
              </div>
            </div>
          </div>

          {/* Manager prep status */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={managerCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                managerCompleted,
                true,
                doesManPlanningExist
              )}`}
              size="lg"
            />
            <div>
              <div className="text-sm font-medium text-gray-700">
                {managerName}'s Prep
              </div>
              <div
                className={`text-xs ${listingHelper.getDualPrepStatusTextColor(
                  managerCompleted,
                  true,
                  doesManPlanningExist
                )}`}
              >
                {managerCompleted
                  ? "Completed"
                  : doesManPlanningExist
                  ? "In Progress"
                  : "Not started yet"}
              </div>
            </div>
          </div>

          {/* Meeting status */}
          <div className="flex items-center">
            <FontAwesomeIcon
              icon={meetingDocCompleted ? faCheckCircle : faTimesCircle}
              className={`mr-2 ${listingHelper.getDualPrepStatusIconColor(
                meetingDocCompleted,
                doesMeetingDocExist
              )}`}
              size="lg"
            />
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-700">
                Meeting Document
              </div>
              <div className="text-xs">
                {doesMeetingDocExist ? (
                  <>
                    {/* If the meeting doc is locked for the employee, then don't show the button */}
                    {isLockDualPrepMeetingForEmployeeEnabled &&
                    (meetingDoc?.statusEnum ===
                      "INITIAL-SAVE-FORM-INCOMPLETE" ||
                      meetingDoc?.statusEnum ===
                        "SAVED-BY-MANAGER-WITHOUT-APPROVING") ? (
                      <span
                        className={`${listingHelper.getDualPrepStatusTextColor(
                          meetingDocCompleted
                        )} font-medium transition-colors text-xs flex items-center`}
                      >
                        Locked
                        <Tooltip
                          content="You'll be able to view this once your manager has filled it in"
                          triggerElement={
                            <span>
                              <FontAwesomeIcon
                                icon={faLock}
                                className="ml-1"
                                size="xs"
                              />
                            </span>
                          }
                        />
                      </span>
                    ) : (
                      <button
                        className={`${listingHelper.getDualPrepStatusTextColor(
                          meetingDocCompleted
                        )} hover:font-semibold underline font-medium transition-colors text-xs`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (meetingDocCompleted) {
                            onDualPrepButtonClick(e, journey, "VIEW-MEETING");
                          } else {
                            onDualPrepButtonClick(e, journey, "UPDATE-MEETING");
                          }
                        }}
                      >
                        {meetingDocCompleted ? "View" : "Continue"}
                      </button>
                    )}
                  </>
                ) : (
                  <span
                    className={listingHelper.getDualPrepStatusTextColor(
                      false,
                      false
                    )}
                  >
                    Not available yet
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Format the dates if they exist
  const formattedSentDate = journey.dateSent
    ? dateHelper.convertUtcDateToLocal(journey.dateSent).toLocaleDateString()
    : "Not available";

  const formattedLastUpdatedDate = journey.dateLastUpdated
    ? dateHelper
        .convertUtcDateToLocal(journey.dateLastUpdated)
        .toLocaleDateString()
    : "Not available";

  const formattedExpectedDate = journey.expectedStartDate
    ? dateHelper
        .convertUtcDateToLocal(journey.expectedStartDate)
        .toLocaleDateString()
    : "Not available";

  const triggerEmpLockOnMeetingDoc =
    journey.isDualPrepJourney &&
    journey.latestAnswerSet != null &&
    journey.latestAnswerSet.isDualPrepMeetingDocAnswerSet &&
    isLockDualPrepMeetingForEmployeeEnabled &&
    (journey.approvalStatusEnum === "INITIAL-SAVE-FORM-INCOMPLETE" ||
      journey.approvalStatusEnum === "SAVED-BY-MANAGER-WITHOUT-APPROVING");

  return (
    <div className="mb-3 rounded-md border border-gray-200 hover:shadow-sm bg-white">
      {/* Header section - consistent for all journey types */}
      <div className="flex justify-between items-center w-full py-3 px-4 bg-gray-50 rounded-md">
        <div className="flex flex-col items-start flex-grow">
          <p className="font-medium text-base text-gray-700">{journey.title}</p>

          {isUpcoming ? (
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="mr-1.5 text-gray-400"
                  size="xs"
                />
                <span>Expected {formattedExpectedDate}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
              <div className="flex items-center">
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className="mr-1.5 text-gray-400"
                  size="xs"
                />
                <span>Sent: {formattedSentDate}</span>
              </div>

              {journey.dateLastUpdated && (
                <>
                  <span className="text-gray-300">•</span>
                  <div className="flex items-center">
                    <FontAwesomeIcon
                      icon={faTimer}
                      className="mr-1.5 text-gray-400"
                      size="xs"
                    />
                    <span>Updated: {formattedLastUpdatedDate}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center">
          <span
            className={`mr-4 text-xs px-2 py-0.5 rounded-full ${listingHelper.getJourneyStatusColor(
              journey
            )}`}
          >
            {listingHelper.getJourneyStatusText(journey)}
          </span>

          {/* Standard button */}
          {!journey.isDualPrepJourney &&
            (isUpcoming ? (
              <Tooltip
                content={t(
                  "Pages.MyDashboard.Conversations.UpcomingUpdateTooltip",
                  "This isn't available until its expected date."
                )}
                triggerElement={
                  <span className="inline-block cursor-not-allowed">
                    <button
                      className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={onStandardButtonClick}
                      disabled
                    >
                      {standardButtonText}
                    </button>
                  </span>
                }
              />
            ) : (
              <button
                className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onStandardButtonClick}
                disabled={disableStandardButton || hasButtonBeenClicked}
              >
                {hasButtonBeenClicked ? (
                  <span className="flex items-center justify-center">
                    <span>{t("Common.PleaseWait")}</span>
                  </span>
                ) : (
                  standardButtonText
                )}
              </button>
            ))}

          {/* Dual Prep button */}
          {journey.isDualPrepJourney &&
            dualPrepDropDownButtonItems.length >= 1 && (
              <>
                {!triggerEmpLockOnMeetingDoc ? (
                  <button
                    className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    disabled={
                      disablePrimaryDualPrepButton || hasButtonBeenClicked
                    }
                    onClick={(e) =>
                      onDualPrepButtonClick(
                        e,
                        journey,
                        dualPrepDropDownButtonItems[0].customArg
                      )
                    }
                  >
                    {hasButtonBeenClicked ? (
                      <span className="flex items-center justify-center">
                        <span>{t("Common.PleaseWait")}</span>
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        {standardButtonText}
                      </span>
                    )}
                  </button>
                ) : (
                  <Tooltip
                    content="You'll be able to view this once your manager has filled it in"
                    triggerElement={
                      <span>
                        <button
                          className="btn-primary text-sm py-1 px-4 w-24 text-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          disabled
                        >
                          Locked
                          <FontAwesomeIcon
                            icon={faLock}
                            className="ml-1"
                            size="xs"
                          />
                        </button>
                      </span>
                    }
                  />
                )}
              </>
            )}
        </div>
      </div>

      {/* Dual prep content section - only for dual prep journeys */}
      {renderDualPrepContent()}
    </div>
  );
}

export default JourneyFormItem;
